import
{
    PaginatedResult,
    PaginationRequest,
} from "@ews/react-data";
import
{
    useL10n
} from "@ews/react-localization-context";
import
{
    IonCol, IonLabel,
} from "@ionic/react";
import
{
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";
import FilterList, { isFilterActive } from "../../FilterList/FilterList";
import { FilterState } from "../../FilterList/types";
import List from "../../List/List";
import { defaultPageRange } from "../../List/Pagination/pagerange";
import { useMyDateFormat } from "../../Login/LoginProvider";
import { usePersistentState } from "../../Persistence";
import { useFetchSystemTextMessagesLog, useSystem } from "../../ReactData/system";
import { useIsMobile } from "../../Responsive";
import DatePicker, { dateRange, dateStringToValue, valueToDateString } from "../../Settings/SettingsComponents/SettingsRangeInput/components/DatePicker/DatePicker";
import SettingsTextInput from "../../Settings/SettingsComponents/SettingsTextInput/SettingsTextInput";
import { Filter } from "../Reports/types/reportTypes";
import { SentTextMessage, SentTextMessageFilterKeys, SentTextMessageLogProps } from "./types";
import { HeaderOption } from "../../List/types";
import { Locale } from "../../../setup/locale";
import style from "./SentTextMessagesLog.module.scss";

type PersistenceFilter = Filter<SentTextMessageFilterKeys> & FilterState;

const defaultRange = dateRange(new Date(), 1);

const ignoreKeys: (keyof PersistenceFilter)[] = ["pageSize", "isFilterOpen", "page", "pageSize", "sortCriteria", "sortOrder"];

const SentTextMessagesLog: React.FC<SentTextMessageLogProps> = ({
    systemId,
    filter,
    onIsOpen,
    onFilterActive
}) =>
{
    const { translate: t } = useL10n();
    const filterDebounceTime = 900;
    const { timezone } = useSystem(systemId || "");

    const filterData = useFetchSystemTextMessagesLog();
    const mobileView = useIsMobile();
    const dateFormat = useMyDateFormat();

    const header: HeaderOption<Locale, SentTextMessage>[] = useMemo(() => [

        {
            title: 'COM_FACPMANAGEMENT_SHARES_ANLAGENNAME',
            key: 'title',
            className: style.systemname,
            both: true,
        },
        {
            title: 'COM_FACPMANAGEMENT_LOGS_SMS_PHONE_NUMBER',
            key: 'telephoneNumber',
            className: style.telephoneNumber,
            both: true,
        },
        {
            title: 'COM_CONTACT_CONTACT_ENTER_MESSAGE_LABEL',
            key: 'message',
            className: style.message,
            both: true,
        },

        {
            title: '[REACT_SENDED]',
            key: 'created',
            className: style.created,
            breakpoint: 834,
            up: true,
            child: ({ created }: SentTextMessage) =>
            {
                return <>
                    {dateFormat(new Date(created!), timezone)}
                </>;
            }
        },
    ], [dateFormat, timezone]);

    const standardFilter: Filter<SentTextMessageFilterKeys, keyof SentTextMessage> = useMemo(() => ({
        created: valueToDateString(defaultRange, timezone),
        sortCriteria: "created",
        sortOrder: "DESC",
        pageSize: defaultPageRange
    }), [timezone]);

    const [result, setResult] = useState<PaginatedResult<SentTextMessage>>({
        currentPage: 0,
        pageSize: standardFilter.pageSize || defaultPageRange,
        totalCountOfEntries: 0,
        totalCountOfPages: 0,
        results: [],
    });

    const [currentState, setState] = usePersistentState<PersistenceFilter>('system:sent-text-messages-log', { ...standardFilter } as PersistenceFilter);


    useEffect(() =>
    {
        setState((pre) => ({ ...pre, created: standardFilter.created }));

    }, [standardFilter, setState]);



    const onPageChange = useCallback((page: PersistenceFilter["page"]) => setState((prev) => ({ ...prev, page })), [setState]);
    const onSortChange = useCallback((sortCriteria: PersistenceFilter["sortCriteria"], sortOrder: PersistenceFilter["sortOrder"]) => setState((prev) => ({ ...prev, sortCriteria, sortOrder })), [setState]);
    const onPageSizeChange = useCallback((pageSize: PersistenceFilter["pageSize"]) => setState((prev) => ({ ...prev, pageSize })), [setState]);

    const onIsFilterOpen = useCallback((state: boolean) => { onIsOpen && onIsOpen(state, "main"); }, [onIsOpen]);
    const onResetFilter = useCallback(() => setState(() => standardFilter), [setState, standardFilter]);

    const isFilterChanged = isFilterActive<PersistenceFilter>(currentState, standardFilter, ignoreKeys);

    useEffect(() =>
    {
        if (filter?.isFilterActive !== isFilterChanged) {
            onFilterActive && onFilterActive(isFilterChanged);
        }
    }, [isFilterChanged, filter, onFilterActive]);


    const getFilteredData = useCallback(async (id: string, filter: PaginationRequest) =>
    {
        const response = await filterData(id, filter);
        if (!response) return;

        setResult(response);

    }, [filterData]);

    useEffect(() =>
    {
        if (systemId) { getFilteredData(systemId, currentState); }
    }, [currentState, getFilteredData, systemId]);

    const sortCriteria =
        //useMemo(() => (
        currentState.sortCriteria ? {
            key: currentState.sortCriteria,
            direction: currentState.sortOrder || "ASC"
        } : undefined;
    //), [currentState.sortCriteria, currentState.sortOrder]);

    return <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <FilterList
            isOpen={filter?.isFilterOpen || false}
            onIsOpen={onIsFilterOpen}
            onReset={onResetFilter}
            noPrint
        >

            <IonCol sizeLg="3" sizeMd="1" sizeSm="1">
                <SettingsTextInput
                    id="telephoneNumber"
                    text="COM_FACPMANAGEMENT_LOGS_SMS_PHONE_NUMBER"
                    debounce={filterDebounceTime}
                    value={currentState["telephoneNumber"]}
                    onChange={(value) =>
                    {
                        const currentFilter = { ...currentState };
                        if (value.length) currentFilter["telephoneNumber"] = value;
                        else delete currentFilter["telephoneNumber"];
                        setState(currentFilter);
                    }}
                />
            </IonCol>
            <IonCol sizeLg="3" sizeMd="1" sizeSm="1">
                <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <IonLabel style={{ maxWidth: "fit-content" }}>{t("TD_DATUM_DRUCK")}</IonLabel>
                </div>
                <DatePicker
                    childText={{ start: "[REACT_START]", end: "[REACT_END]" }}
                    value={dateStringToValue(currentState.created!)}
                    divider={false}
                    position="row"
                    onChange={(range) =>
                    {
                        const currentFilter = { ...currentState };
                        currentFilter.created = valueToDateString(range, timezone);
                        setState({ ...currentFilter });
                    }}

                />
            </IonCol>
            <IonCol></IonCol>
        </FilterList >
        <List
            rows={result.results}
            rowClass={style.row}
            sort={sortCriteria}
            onSort={onSortChange}
            onPageChange={onPageChange}
            onPageSize={onPageSizeChange}
            pageSize={result.pageSize}
            currentPage={result.currentPage}
            numberOfPages={result.totalCountOfPages}
            maxHeight={mobileView ? "86vh" : "47vh"}
            noLink
            headers={header}
        />
    </div>;
};

export default SentTextMessagesLog;
